/**
 * The market seam on the main thread (M4 spec §5.4-§5.7, seams S-M2-2, S-M2-3, S-M2-4; plan I3): the
 * `StrategicMarket` M2's `quote()` and trade actions price and trade through when the Reach runs.
 *
 * - **Rows** come from the cached `StationView`s: every station of the latest system view (`onSystemView`, at
 *   most 1 Hz) and manifest (`onManifest`), and the station a trade just touched (`stationView` after each
 *   commit). `onChange` tells the career runtime to republish its screens.
 * - **Quotes** are computed here with the worker's own pure curve (`strategic/economy/prices.ts`) from the cached
 *   row, so a screen never waits on the worker. The row carries the fence multiplier (derived from its
 *   `sellUnit`) and the reservations (derived from its `buyUnit`); the M2 standing modifier goes on top. For
 *   `qty > 0` the unit is the quantity's average (`total / qty`), exactly what the worker compares with
 *   `expectedUnit`; for `qty = 0` it is the marginal price.
 * - **Commit** sends `playerTrade` to the worker. It executes within the 2% tolerance or refuses with a typed
 *   error; either way the station's row is refreshed before the promise settles, so a `priceMoved` leaves the
 *   fresh price on screen for the next attempt.
 * - **Station ids** (S-M2-4): a career station id resolves to a strategic station of the cached views: the same
 *   id, then the M2 bridge's slug of it (`sys-33-st0` ↔ `sys_33_st0`), then for M2's `hub`, `refinery` and
 *   `scrapyard` the station whose template plays it (`templates.json` `m2Id`, the `m2StationMap` rule).
 * - **Reports** (S-M2-3): ore the player mined → `playerMined` against the local system's field holding the most
 *   of that ore (M2's `oreCollected` has no field id; D-TBD in the I3 report); a sold salvage component →
 *   `playerSoldComponent` (its ware yield goes into the station's stock). Fire and forget: failures are logged.
 * - **Tariffs (M5 spec §5.4, §14.4; plan C15):** a claimed system's holder charges its tariff `t`
 *   (`StationView.tariff`) on the player's trades: the rows, quotes and executed trades show what the player pays or
 *   gets, `unit × (1 + t)` bought and `unit × (1 − t)` sold. The worker's `playerTrade` prices without the tariff,
 *   so `commit` sends the pre-tariff `expectedUnit` and scales the executed unit and total back (the Career books
 *   those). `tariffLine` gives the row tooltip ("Tethys tariff 6%"). The holder is not credited the player's tariff
 *   (that needs a core change; D-TBD in the C15 report).
 * - **Disabled stations (M5 spec §14.4):** a station a war disabled (`StationView.disabledBy`) has its market
 *   closed: no rows, no quotes, and `commit` refuses with `notTraded` without asking the worker.
 * - **Sink draws (M8 plan §4.5 SNK-3a):** `quoteDraw` and `draw` are the pilot's repair plating and torpedo weapon
 *   parts. `quoteDraw` quotes what `playerDraw` would take: the wanted tonnes clamped to the station's stock above
 *   the sink's `reserveFill` × capacity (`sinks.json`, the AI's reserve rule), at cost at the corporation's own
 *   stations. `draw` sends `playerDraw` and applies the tariff exactly as `commit` does (the pre-tariff
 *   `expectedUnit` and budget go out, the executed unit and total come back scaled). A draw is partial when the
 *   station is short or the budget runs out; `got` is what the Career books, `total` what it debits.
 *
 * Nothing here runs per frame: quotes run on UI renders and trades, views arrive at most once a second.
 */
import { logger } from '../core/logger'
import type { Quote, WareQuoteSource } from '../economy/quote'
import sinksJson from '../data/strategic/sinks.json'
import templatesJson from '../data/strategic/templates.json'
import waresJson from '../data/strategic/wares.json'
import { m2StationId } from '../bridge/m2/stations'
import { loadFactionsConfig, type FactionsConfig } from '../galaxy/layout/config'
import {
  buyTotal,
  buyUnit,
  buyUnitAtFill,
  clampBuyQty,
  clampSellQty,
  maxPrice,
  minPrice,
  priceCurve,
  sellTotal,
  sellUnit,
  standingMult,
} from '../strategic/economy/prices'
import type { DrawResult, PlayerDrawCommand, PlayerDrawSink } from '../strategic/economy/market'
import {
  PLAYER_FACTION_ID,
  SIDE,
  type CommandResult,
  type LocalManifest,
  type MarketRow,
  type StationView,
  type StrategicCommand,
  type SystemEconomyView,
  type TradeQuote,
  type TradeResult,
  type TradeSide,
} from '../strategic/types'
import { isWareId, wareIndex, type ComponentType, type WareId } from '../strategic/wares'

/** A local quote (spec §5.6): `unit` per t, `total` for the (clamped) quantity, fills before and after. */
export interface MarketQuote {
  /** Tonnes the quote covers (`qty` clamped to what fits). */
  qty: number
  unit: number
  total: number
  fillBefore: number
  fillAfter: number
  /** True when `qty` was clamped (no room at the station, or not enough stock). */
  clamped: boolean
}

/** What the market needs from the strategic client (`StrategicClient` satisfies it). */
export interface MarketClient {
  readonly systemView: SystemEconomyView | null
  readonly manifest: LocalManifest | null
  onSystemView(fn: (view: SystemEconomyView) => void): () => void
  onManifest(fn: (manifest: LocalManifest) => void): () => void
  stationView(stationId: string): Promise<StationView>
  command(cmd: StrategicCommand): Promise<CommandResult>
}

export interface StrategicMarket {
  /** The strategic station id a career station id plays, or null when no cached station matches. */
  resolve(stationId: string): string | null
  /** The station's market rows (career or strategic id), or an empty list when it is not cached. */
  rows(stationId: string): readonly MarketRow[]
  /** The local quote, or null when the station is unknown or does not trade `ware` on `side`. */
  quote(stationId: string, ware: WareId, qty: number, side: TradeSide, standing?: number): MarketQuote | null
  /** Sends the trade to the worker (commit first, spec §5.6); refreshes the station's row before settling. */
  commit(stationId: string, ware: WareId, qty: number, side: TradeSide, expectedUnit: number, standing?: number): Promise<TradeResult>
  /**
   * The quote for a sink draw of up to `wantT` t (M8 SNK-3a): clamped to the stock above the sink's reserve (`clamped`
   * when short; `qty` 0 at the reserve), under the tariff, at cost at the corporation's own stations. Null when the
   * station is unknown, closed or doesn't sell the ware to the pilot.
   */
  quoteDraw(stationId: string, ware: WareId, wantT: number, sink: PlayerDrawSink, standing?: number): MarketQuote | null
  /**
   * Sends `playerDraw` (M8 SNK-3a): up to `wantT` t at no more than `expectedUnit` (+ the 2% tolerance, tariff
   * included), costing at most `budget` credits (tariff included) when given. Refreshes the station's row before
   * settling. `got` 0 means the budget covered nothing.
   */
  draw(stationId: string, ware: WareId, wantT: number, expectedUnit: number, sink: PlayerDrawSink, standing?: number, budget?: number): Promise<DrawResult>
  /** Reports ore the player mined in the local system (S-M2-3). */
  reportMined(ware: WareId, tonnes: number): void
  /** Reports a salvage component the player sold at `stationId` (S-M2-3, spec §5.7). */
  reportComponentSold(stationId: string, component: ComponentType, condition: number): void
  /** The row tooltip's tariff line ("Tethys tariff 6%"), or null when the station charges no tariff (M5). */
  tariffLine(stationId: string): string | null
  /** Whether the station's market and services are closed (a war disabled it, M5); false when it is unknown. */
  closed(stationId: string): boolean
  /** Called whenever the cached rows change (a new view or a refreshed station). */
  onChange(fn: () => void): () => void
  /** Drops the client subscriptions. */
  dispose(): void
}

const BASE_PRICE: readonly number[] = waresJson.wares.map((w) => w.basePrice)

/** M2 station id → the strategic template ids that play it (`templates.json` `m2Id`). */
const TEMPLATES_BY_M2: ReadonlyMap<string, ReadonlySet<string>> = (() => {
  const map = new Map<string, Set<string>>()
  for (const t of templatesJson.templates) {
    if (t.m2Id === null) continue
    const set = map.get(t.m2Id) ?? new Set<string>()
    set.add(t.id)
    map.set(t.m2Id, set)
  }
  return map
})()

function clamp01(x: number): number {
  return x > 0 ? (x < 1 ? x : 1) : 0
}

/**
 * The quote for `qty` t of the row's ware from the player's side, on the worker's curve (`quoteStation` +
 * `playerTrade`): the fence multiplier comes from the row's `sellUnit`, the reserved tonnes from its `buyUnit`.
 * Null when the station does not trade the ware on `side`.
 */
export function quoteRow(row: MarketRow, qty: number, side: TradeSide, standing = 0): MarketQuote | null {
  const w = wareIndex(row.ware)
  const base = BASE_PRICE[w]
  const stock = row.stock
  const cap = row.capacity
  const q = qty > 0 ? qty : 0
  if (side === 'sell') {
    if (!row.buys || row.sellUnit === null) return null
    const curve = sellUnit(base, stock, cap)
    const fence = curve > 0 ? row.sellUnit / curve : 1
    const mult = fence * standingMult(standing, SIDE.sell)
    const qq = clampSellQty(stock, cap, q)
    const total = sellTotal(base, stock, cap, qq) * mult
    return {
      qty: qq,
      unit: qq > 0 ? total / qq : curve * mult,
      total,
      fillBefore: cap > 0 ? clamp01(stock / cap) : 0,
      fillAfter: cap > 0 ? clamp01((stock + qq) / cap) : 0,
      clamped: qq < q,
    }
  }
  if (!row.sells || row.buyUnit === null) return null
  const reserved = reservedOf(row, base)
  const mult = standingMult(standing, SIDE.buy)
  const qq = clampBuyQty(stock, reserved, q)
  // 5(e) MC-e4 (RD6): a floored import row (the Export Terminal's alloy) charges at least floorUnit × qty, as the worker does.
  const floor = row.floorUnit ?? 0
  const curve = buyTotal(base, stock, reserved, cap, qq)
  const total = (curve > floor * qq ? curve : floor * qq) * mult
  const marginal = buyUnit(base, stock, reserved, cap)
  return {
    qty: qq,
    unit: qq > 0 ? total / qq : (marginal > floor ? marginal : floor) * mult,
    total,
    fillBefore: cap > 0 ? clamp01((stock - reserved) / cap) : 0,
    fillAfter: cap > 0 ? clamp01((stock - reserved - qq) / cap) : 0,
    clamped: qq < q,
  }
}

/** The reserved tonnes a buy row's `buyUnit` implies (`B` inverted to the available fill). */
function reservedOf(row: MarketRow, base: number): number {
  const cap = row.capacity
  if (row.buyUnit === null || !(cap > 0)) return 0
  // A floored row's buyUnit is the floor, not the curve, so it carries its reserved tonnes (MC-e4).
  if (row.reserved !== undefined) return row.reserved
  const hi = maxPrice(base)
  const lo = minPrice(base)
  if (!(hi > lo)) return 0
  // At fill 0 (nothing available beyond the reservations) every reservation level gives the same price.
  if (row.buyUnit >= buyUnitAtFill(base, 0) - 1e-9 * hi) return row.stock
  const fill = clamp01((row.buyUnit / priceCurve().buyMarkup - hi) / (lo - hi))
  const reserved = row.stock - fill * cap
  return reserved > 0 ? reserved : 0
}

function refusalQuote(error: 'unknownStation' | 'notTraded'): TradeResult {
  return { ok: false, error }
}

/** A draw never takes a station below this fill of its capacity (`sinks.json`, the AI's reserve rule; SNK-3a). */
const DRAW_RESERVE: Readonly<Record<PlayerDrawSink, number>> = {
  playerRepair: sinksJson.repairs.reserveFill,
  playerTorpedo: sinksJson.munitions.reserveFill,
}

/**
 * The pre-tariff quote for a sink draw of up to `wantT` t from the row (`playerDraw`'s rule, M8 SNK-3a): the tonnes
 * above `reserveFill` × capacity, on the buy curve with the standing modifier, or at cost (`atCost`: the
 * corporation's own station). `clamped` when fewer than `wantT` t fit. Null when the row doesn't sell to the pilot.
 */
export function quoteDrawRow(row: MarketRow, wantT: number, sink: PlayerDrawSink, standing = 0, atCost = false): MarketQuote | null {
  if (!row.sells || row.buyUnit === null) return null
  const w = wareIndex(row.ware)
  const base = BASE_PRICE[w]
  const want = wantT > 0 ? wantT : 0
  const room = row.stock - reservedOf(row, base) - DRAW_RESERVE[sink] * row.capacity
  const q = want < room ? want : room > 0 ? room : 0
  const quote = quoteRow(row, q, 'buy', standing)
  if (quote === null) return null
  if (atCost) {
    quote.unit = base
    quote.total = quote.qty * base
  }
  quote.clamped = quote.qty < want
  return quote
}

/** An upper bound on the tariff fraction (the spec clamps it to 10%); keeps `1 − t` well above 0 for the division. */
const MAX_TARIFF = 0.5

/** The tariff fraction the player pays at a station (M5 spec §5.4): the holder's `tariff`, 0 when unclaimed. */
export function tariffOf(view: Pick<StationView, 'holder' | 'tariff'>): number {
  if (view.holder === null) return 0
  const t = view.tariff
  return t > 0 ? (t < MAX_TARIFF ? t : MAX_TARIFF) : 0
}

/** The tariff's factor on a unit price: the buyer pays `× (1 + t)`, the seller receives `× (1 − t)`. */
export function tariffMult(t: number, side: TradeSide): number {
  return side === 'buy' ? 1 + t : 1 - t
}

/** A row as the player sees it under tariff `t`: `buyUnit × (1 + t)`, `sellUnit × (1 − t)`. */
export function tariffRow(row: MarketRow, t: number): MarketRow {
  if (!(t > 0)) return row
  return {
    ...row,
    buyUnit: row.buyUnit === null ? null : row.buyUnit * tariffMult(t, 'buy'),
    sellUnit: row.sellUnit === null ? null : row.sellUnit * tariffMult(t, 'sell'),
  }
}

/** Scales a quote's `unit` and `total` by the tariff (in place); returns it. */
export function applyTariff<Q extends { unit: number; total: number }>(q: Q, t: number, side: TradeSide): Q {
  if (!(t > 0)) return q
  const m = tariffMult(t, side)
  q.unit *= m
  q.total *= m
  return q
}

let cachedFactions: FactionsConfig | null = null

/** The tooltip line "Tethys tariff 6%" (the holder's short name from M3's factions, else its id), or null at 0. */
export function tariffText(view: Pick<StationView, 'holder' | 'tariff'>, factions?: FactionsConfig): string | null {
  const t = tariffOf(view)
  if (view.holder === null || !(t > 0)) return null
  const f = factions ?? (cachedFactions ??= loadFactionsConfig())
  const name = Object.hasOwn(f.factions, view.holder) ? f.factions[view.holder as keyof FactionsConfig['factions']].short : view.holder
  return `${name} tariff ${Math.round(t * 100)}%`
}

export function createStrategicMarket(client: MarketClient): StrategicMarket {
  /** Strategic station id → its latest view. */
  const stations = new Map<string, StationView>()
  const listeners = new Set<() => void>()

  function changed(): void {
    for (const fn of listeners) {
      try {
        fn()
      } catch (err) {
        logger.error('strategic market: change listener failed', err)
      }
    }
  }

  function absorb(list: readonly StationView[]): void {
    for (const v of list) stations.set(v.id, v)
    changed()
  }

  if (client.manifest !== null) for (const v of client.manifest.stations) stations.set(v.id, v)
  if (client.systemView !== null) for (const v of client.systemView.stations) stations.set(v.id, v)
  const offView = client.onSystemView((view) => absorb(view.stations))
  const offManifest = client.onManifest((manifest) => absorb(manifest.stations))

  function resolve(stationId: string): string | null {
    if (stations.has(stationId)) return stationId
    for (const id of stations.keys()) if (m2StationId(id) === stationId) return id
    const templates = TEMPLATES_BY_M2.get(stationId)
    if (templates === undefined) return null
    const system = client.manifest?.systemId ?? client.systemView?.systemId ?? null
    for (const v of stations.values()) {
      if (templates.has(v.template) && (system === null || v.systemId === system)) return v.id
    }
    return null
  }

  /** The cached view of a career or strategic station id. */
  function viewOf(stationId: string): StationView | null {
    const id = resolve(stationId)
    return id === null ? null : (stations.get(id) ?? null)
  }

  /** The player-facing rows of each view under its tariff (made once per view object). */
  const tariffed = new WeakMap<StationView, readonly MarketRow[]>()
  function playerRows(view: StationView): readonly MarketRow[] {
    if (view.disabledBy !== null) return []
    const t = tariffOf(view)
    if (!(t > 0)) return view.wares
    let rows = tariffed.get(view)
    if (rows === undefined) {
      rows = view.wares.map((r) => tariffRow(r, t))
      tariffed.set(view, rows)
    }
    return rows
  }

  async function refresh(id: string): Promise<void> {
    try {
      const view = await client.stationView(id)
      stations.set(view.id, view)
      changed()
    } catch (err) {
      logger.warn(`strategic market: could not refresh station ${id}`, err)
    }
  }

  function fieldFor(ware: WareId): { systemId: string; fieldId: string } | null {
    const view = client.systemView
    const local = client.manifest?.systemId ?? null
    // The system view follows a watched system when a map watches one: report only against the local system.
    if (view === null || (local !== null && view.systemId !== local)) return null
    let best: string | null = null
    let most = -1
    for (const f of view.fields) {
      const r = f.reserves[ware]
      if (r !== undefined && r > most) {
        most = r
        best = f.id
      }
    }
    return best === null ? null : { systemId: view.systemId, fieldId: best }
  }

  function report(what: string, cmd: StrategicCommand, then?: () => Promise<void>): void {
    client
      .command(cmd)
      .then(
        (r) => {
          if (!r.ok) logger.warn(`strategic market: ${what} refused (${r.error})`)
        },
        (err: unknown) => logger.warn(`strategic market: ${what} failed`, err),
      )
      .then(then)
      .catch((err: unknown) => logger.warn(`strategic market: ${what} follow-up failed`, err))
  }

  return {
    resolve,
    rows(stationId: string): readonly MarketRow[] {
      const view = viewOf(stationId)
      return view === null ? [] : playerRows(view)
    },
    quote(stationId: string, ware: WareId, qty: number, side: TradeSide, standing = 0): MarketQuote | null {
      const view = viewOf(stationId)
      if (view === null || view.disabledBy !== null) return null
      const r = view.wares.find((w) => w.ware === ware)
      if (r === undefined) return null
      const q = quoteRow(r, qty, side, standing)
      return q === null ? null : applyTariff(q, tariffOf(view), side)
    },
    async commit(stationId: string, ware: WareId, qty: number, side: TradeSide, expectedUnit: number, standing = 0): Promise<TradeResult> {
      const id = resolve(stationId)
      if (id === null) return refusalQuote('unknownStation')
      if (!isWareId(ware)) return refusalQuote('notTraded')
      const view = stations.get(id)
      if (view !== undefined && view.disabledBy !== null) return refusalQuote('notTraded')
      // The worker prices without the tariff: send the pre-tariff unit, scale what it executed back.
      const tariff = view === undefined ? 0 : tariffOf(view)
      const mult = tariffMult(tariff, side)
      const r = await client.command({ type: 'playerTrade', stationId: id, ware, qty, side, expectedUnit: expectedUnit / mult, standing })
      await refresh(id)
      if (r.ok) {
        const t = r.trade
        return t === undefined ? { ok: true, qty, total: expectedUnit * qty, unit: expectedUnit } : { ok: true, qty: t.qty, total: t.total * mult, unit: t.unit * mult }
      }
      const error = r.error
      if (error === 'priceMoved' || error === 'noStock' || error === 'noRoom' || error === 'notTraded' || error === 'unknownStation') {
        const quote: TradeQuote | undefined = r.quote === undefined ? undefined : applyTariff({ ...r.quote }, tariff, side)
        return quote === undefined ? { ok: false, error } : { ok: false, error, quote }
      }
      logger.warn(`strategic market: playerTrade refused with ${error}`)
      return { ok: false, error: 'notTraded' }
    },
    quoteDraw(stationId: string, ware: WareId, wantT: number, sink: PlayerDrawSink, standing = 0): MarketQuote | null {
      const view = viewOf(stationId)
      if (view === null || view.disabledBy !== null) return null
      const r = view.wares.find((w) => w.ware === ware)
      if (r === undefined) return null
      const atCost = view.owner === PLAYER_FACTION_ID
      const q = quoteDrawRow(r, wantT, sink, atCost ? 0 : standing, atCost)
      return q === null ? null : applyTariff(q, tariffOf(view), 'buy')
    },
    async draw(stationId: string, ware: WareId, wantT: number, expectedUnit: number, sink: PlayerDrawSink, standing = 0, budget?: number): Promise<DrawResult> {
      const id = resolve(stationId)
      if (id === null) return { ok: false, error: 'unknownStation' }
      if (!isWareId(ware)) return { ok: false, error: 'notTraded' }
      const view = stations.get(id)
      if (view !== undefined && view.disabledBy !== null) return { ok: false, error: 'notTraded' }
      // The worker prices without the tariff: send the pre-tariff unit and budget, scale what it executed back.
      const tariff = view === undefined ? 0 : tariffOf(view)
      const mult = tariffMult(tariff, 'buy')
      const cmd: PlayerDrawCommand = { type: 'playerDraw', stationId: id, ware, wantT, expectedUnit: expectedUnit / mult, standing, sink }
      if (budget !== undefined) cmd.maxTotal = budget > 0 ? budget / mult : 0
      const r = await client.command(cmd)
      await refresh(id)
      if (r.ok) {
        const t = r.trade
        if (t === undefined) {
          logger.warn('strategic market: playerDraw returned no trade')
          return { ok: false, error: 'notTraded' }
        }
        return { ok: true, got: t.qty, total: t.total * mult, unit: t.unit * mult }
      }
      const error = r.error
      if (error === 'priceMoved' || error === 'noStock' || error === 'notTraded' || error === 'unknownStation') {
        return r.quote === undefined ? { ok: false, error } : { ok: false, error, quote: applyTariff({ ...r.quote }, tariff, 'buy') }
      }
      logger.warn(`strategic market: playerDraw refused with ${error}`)
      return { ok: false, error: 'notTraded' }
    },
    reportMined(ware: WareId, tonnes: number): void {
      if (!(tonnes > 0)) return
      const at = fieldFor(ware)
      if (at === null) return
      report('playerMined', { type: 'playerMined', systemId: at.systemId, fieldId: at.fieldId, ware, qty: tonnes })
    },
    reportComponentSold(stationId: string, component: ComponentType, condition: number): void {
      const id = resolve(stationId)
      if (id === null) return
      report('playerSoldComponent', { type: 'playerSoldComponent', stationId: id, component, condition }, () => refresh(id))
    },
    tariffLine(stationId: string): string | null {
      const view = viewOf(stationId)
      return view === null ? null : tariffText(view)
    },
    closed(stationId: string): boolean {
      const view = viewOf(stationId)
      return view !== null && view.disabledBy !== null
    },
    onChange(fn: () => void): () => void {
      listeners.add(fn)
      return () => listeners.delete(fn)
    },
    dispose(): void {
      offView()
      offManifest()
      listeners.clear()
      stations.clear()
    },
  }
}

const markets = new WeakMap<MarketClient, StrategicMarket>()

/** The one market per client (made on first use: the career runtime asks for it lazily). */
export function strategicMarketFor(client: MarketClient): StrategicMarket {
  let m = markets.get(client)
  if (m === undefined) {
    m = createStrategicMarket(client)
    markets.set(client, m)
  }
  return m
}

/**
 * The `WareQuoteSource` the career runtime installs behind M2's `quote()` (seam S-M2-2): active while `market()`
 * returns a market; a ware at a station the market resolves is quoted on the strategic curve (the total rounded to
 * whole credits, as M2's are; `satBefore`/`satAfter` carry the fill before and after, `qty` the clamped tonnes),
 * anything else falls back.
 */
export function m2QuoteSource(market: () => StrategicMarket | null): WareQuoteSource {
  return {
    get active(): boolean {
      return market() !== null
    },
    quoteWare(stationId, ware, qty, side, standing): Quote | null | undefined {
      const m = market()
      if (m === null || m.resolve(stationId) === null) return undefined
      const q = m.quote(stationId, ware, qty, side, standing)
      if (q === null) return null
      return { unit: q.unit, total: Math.round(q.total), satBefore: q.fillBefore, satAfter: q.fillAfter, qty: q.qty }
    },
  }
}
